import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
  Typography,
  Divider,
  Grid,
  Fade,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import axiosInstance from '../../../axiosInstance';
import { SHOW_SHOPIFY_TAXONOMY } from '../../../config/featureFlags';
import ShopifyTaxonomyPicker from './ShopifyTaxonomyPicker';

export const OnboardingForm = ({ apiKey, setSnackNotification, onTenantCreated }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showProcessingMessage, setShowProcessingMessage] = useState(false);
  const [tenantName, setTenantName] = useState('');
  const [tenantWebsiteUrl, setTenantWebsiteUrl] = useState('');
  const [datafeedType, setDatafeedType] = useState('FileUpload');
  const [forceSkipScrape, setForceSkipScrape] = useState(false);
  const [skipConverter, setSkipConverter] = useState(false);
  const [skipProductsImport, setSkipProductsImport] = useState(false);
  const [forceMaxStockCount, setForceMaxStockCount] = useState(false);
  const [shopifyDomain, setShopifyDomain] = useState('');
  const [shopifyApiKey, setShopifyApiKey] = useState('');
  const [fileDataType, setFileDataType] = useState('JSON');
  const [fileName, setFileName] = useState(null);
  const [httpDataType, setHttpDataType] = useState('JSON');
  const [datafeedUrl, setDatafeedUrl] = useState('');
  const [demoAccount, setDemoAccount] = useState(false);
  const [selectedTaxonomies, setSelectedTaxonomies] = useState([]);

  const isShopify = datafeedType === 'Shopify';
  const isFileUpload = datafeedType === 'FileUpload';
  const isHttp = datafeedType === 'Http';

  const testShopifyConnection = async () => {
    try {
      await axiosInstance.post(
        '/OnboardingTenant/test-shopify-connection',
        { ShopifyDomain: shopifyDomain, ShopifyApiKey: shopifyApiKey },
        { headers: { 't-api-key': apiKey } }
      );
    } catch (error) {
      setSnackNotification({
        open: true,
        message: "Failed to validate tenant's Shopify credentials",
        severity: 'error',
      });
      throw error;
    }
  };

  const handleCreateTenant = async () => {
    const createdTenantName = tenantName;

    const payload = {
      Name: tenantName,
      WebsiteUrl: tenantWebsiteUrl,
      IsShopifyTenant: isShopify,
      ShopifyDomain: isShopify ? shopifyDomain : '',
      ShopifyApiKey: isShopify ? shopifyApiKey : '',
      datafeedType: datafeedType,
      ForceSkipScrape: forceSkipScrape,
      skipConverter: skipConverter,
      SkipProductsImport: skipProductsImport,
      DemoAccount: demoAccount,
      ShopifyTaxonomyIds: skipProductsImport ? [] : selectedTaxonomies.map((taxonomy) => taxonomy.id),
      ...(isFileUpload ? { dataType: fileDataType } : {}),
      ...(isHttp ? { datafeedUrl: datafeedUrl, dataType: httpDataType } : {}),
    };

    try {
      await axiosInstance.post('/onboardingTenant/onboard-v2', payload, {
        headers: { 't-api-key': apiKey },
        timeout: 0,
      });

      setSnackNotification({
        open: true,
        message: 'Tenant created successfully!',
        severity: 'success',
      });

      setTimeout(() => {
        setIsSubmitting(false);
        if (onTenantCreated) {
          onTenantCreated(createdTenantName);
        }
      }, 5000);
    } catch (error) {
      setIsSubmitting(false);
      const errMsg = error.response?.data?.message || 'Error creating tenant';
      setSnackNotification({
        open: true,
        message: errMsg,
        severity: 'error',
      });
      console.error(errMsg);
      throw error;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setShowProcessingMessage(false);

    setTimeout(() => {
      setShowProcessingMessage(true);
    }, 2000);

    try {
      if (isShopify && !skipProductsImport) {
        await testShopifyConnection();
      }
      await handleCreateTenant();
    } catch (error) {
      console.error('Error onboarding tenant:', error);
      setIsSubmitting(false);
      setShowProcessingMessage(false);
    }
  };

  const isFormValid =
    tenantName.trim() !== '' &&
    tenantWebsiteUrl.trim() !== '' &&
    (skipProductsImport ||
      ((!isShopify || (shopifyDomain.trim() !== '')) &&
        (!isHttp || datafeedUrl.trim() !== '')));

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFileName(e.target.files[0].name);
    } else {
      setFileName(null);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      borderRadius: "16px",
      backgroundColor: "#fff",
      "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
      "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
      "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
    },
  };

  const primaryButtonSx = {
    minHeight: 44,
    px: 4,
    borderRadius: "14px",
    textTransform: "none",
    fontWeight: 700,
    color: "#fff",
    background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
    boxShadow: "0 14px 30px rgba(255, 139, 71, 0.28)",
    "&:hover": {
      background: "linear-gradient(180deg, #ffa97b 0%, #ff7c34 100%)",
      boxShadow: "0 18px 36px rgba(255, 139, 71, 0.34)",
    },
  };

  return (
      <form onSubmit={(e) => e.preventDefault()}>
      <Box>
        <Typography variant="subtitle2" color="text.secondary" fontWeight={700} mb={1.5}>
          Tenant Information
        </Typography>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Tenant Name"
              value={tenantName}
              onChange={(e) => setTenantName(e.target.value)}
              fullWidth
              size="small"
              required
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Website URL"
              value={tenantWebsiteUrl}
              onChange={(e) => setTenantWebsiteUrl(e.target.value)}
              fullWidth
              size="small"
              required
              sx={fieldSx}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5, borderColor: "rgba(255, 132, 76, 0.10)" }} />

        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            fontWeight={700}
            sx={{ visibility: skipProductsImport ? "hidden" : "visible" }}
          >
            Data Feed
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={skipProductsImport}
                onChange={(e) => setSkipProductsImport(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2">Skip products import</Typography>}
            sx={{ mr: 0 }}
          />
        </Box>

        {!skipProductsImport && (
          <>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel id="datafeed-type-label">Feed Type</InputLabel>
                  <Select
                    labelId="datafeed-type-label"
                    label="Feed Type"
                    value={datafeedType}
                    onChange={(e) => setDatafeedType(e.target.value)}
                  >
                    <MenuItem value="Shopify">Shopify</MenuItem>
                    <MenuItem value="FileUpload">File Upload</MenuItem>
                    <MenuItem value="Http">Http</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {isShopify && (
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Shopify Domain"
                    placeholder="myapp.myshopify.com"
                    value={shopifyDomain}
                    onChange={(e) => setShopifyDomain(e.target.value)}
                    fullWidth
                    size="small"
                    required={isShopify}
                    sx={fieldSx}
                  />
                </Grid>
              )}

              {isFileUpload && (
                <>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small" sx={fieldSx}>
                      <InputLabel id="file-data-type-label">Data Type</InputLabel>
                      <Select
                        labelId="file-data-type-label"
                        label="Data Type"
                        value={fileDataType}
                        onChange={(e) => setFileDataType(e.target.value)}
                      >
                        <MenuItem value="JSON">JSON</MenuItem>
                        <MenuItem value="CSV">CSV</MenuItem>
                        <MenuItem value="XML">XML</MenuItem>
                        <MenuItem value="TXT">TXT</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <Box display="flex" gap={1} alignItems="center" sx={{ minHeight: 56 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        sx={{
                          whiteSpace: 'nowrap',
                          minHeight: 42,
                          borderRadius: "14px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: "#f97316",
                          borderColor: "rgba(249, 115, 22, 0.26)",
                          backgroundColor: "rgba(255, 247, 241, 0.86)",
                        }}
                      >
                        Upload file
                        <input type="file" hidden onChange={handleFileChange} accept=".json,.csv,.xml,.txt" />
                      </Button>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
                        {fileName || 'No file selected'}
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}

              {isHttp && (
                <>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small" sx={fieldSx}>
                      <InputLabel id="http-data-type-label">Data Type</InputLabel>
                      <Select
                        labelId="http-data-type-label"
                        label="Data Type"
                        value={httpDataType}
                        onChange={(e) => setHttpDataType(e.target.value)}
                      >
                        <MenuItem value="JSON">JSON</MenuItem>
                        <MenuItem value="CSV">CSV</MenuItem>
                        <MenuItem value="XML">XML</MenuItem>
                        <MenuItem value="TXT">TXT</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      label="Data Feed URL"
                      value={datafeedUrl}
                      onChange={(e) => setDatafeedUrl(e.target.value)}
                      fullWidth
                      size="small"
                      required={isHttp}
                      sx={fieldSx}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            {SHOW_SHOPIFY_TAXONOMY && (
              <ShopifyTaxonomyPicker
                value={selectedTaxonomies}
                onChange={setSelectedTaxonomies}
                disabled={isSubmitting}
                sx={{ mb: 2 }}
              />
            )}

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                p: 1.75,
                bgcolor: 'rgba(255, 247, 241, 0.72)',
                borderRadius: 2,
                border: '1px solid rgba(255, 132, 76, 0.10)',
              }}
            >
              <FormControlLabel
                control={<Checkbox checked={forceSkipScrape} onChange={(e) => setForceSkipScrape(e.target.checked)} size="small" />}
                label={<Typography variant="body2">Skip web scraper</Typography>}
              />
              <FormControlLabel
                control={<Checkbox checked={skipConverter} onChange={(e) => setSkipConverter(e.target.checked)} size="small" />}
                label={<Typography variant="body2">Skip AI converter</Typography>}
              />
              <FormControlLabel
                control={<Checkbox checked={demoAccount} onChange={(e) => setDemoAccount(e.target.checked)} size="small" />}
                label={<Typography variant="body2">Demo account</Typography>}
              />
              {(isFileUpload || isHttp) && (
                <FormControlLabel
                  control={<Checkbox checked={forceMaxStockCount} onChange={(e) => setForceMaxStockCount(e.target.checked)} size="small" />}
                  label={<Typography variant="body2">Force max stock (999)</Typography>}
                />
              )}
            </Box>
          </>
        )}

        <Divider sx={{ my: 2.5, borderColor: "rgba(255, 132, 76, 0.10)" }} />

        <Box display="flex" justifyContent="flex-end">
          <Button
            type="button"
            variant="contained"
            disabled={!isFormValid || isSubmitting}
            onClick={handleSubmit}
            sx={primaryButtonSx}
          >
            {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Create Tenant'}
          </Button>
        </Box>

        <Fade in={showProcessingMessage}>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            mt={2}
            p={1.75}
            sx={{
              bgcolor: 'rgba(255, 247, 241, 0.88)',
              borderRadius: 2,
              border: '1px solid rgba(255, 132, 76, 0.14)',
            }}
          >
            <InfoOutlined sx={{ color: '#f97316', fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: "#9a3412" }}>
              The tenant onboard is being processed. You can check the status in the <strong>Importer Status</strong> tab.
            </Typography>
          </Box>
        </Fade>
      </Box>
    </form>
  );
};
